/**
 * Strategy: `v2-pipeline`.
 *
 * The default strategy. Dispatches every intent to its typed pipeline builder
 * in `../pipelines/*` — the multi-call orchestration the utility is about.
 *
 * Every intent kind has a real builder. Adding a new kind to `RouteOpIntent`
 * fails compilation here through `assertNever`.
 */

import { assertNever } from 'types/entities/shared/result'

import { buildAssignPipeline } from '../pipelines/assign'
import { buildChangeDatePipeline } from '../pipelines/changeDate'
import { buildChangeDayOfWeekPipeline } from '../pipelines/changeDayOfWeek'
import { buildCompletePipeline } from '../pipelines/complete'
import { buildMasterPublishPipeline } from '../pipelines/masterPublish'
import { buildMovePipeline } from '../pipelines/move'
import { buildResequencePipeline } from '../pipelines/resequence'
import { buildRestoreDailyVersionPipeline } from '../pipelines/restoreDailyVersion'
import { buildRestoreMasterVersionPipeline } from '../pipelines/restoreMasterVersion'
import { buildSyncDailyToMasterPipeline } from '../pipelines/syncDailyToMaster'
import { buildUnassignPipeline } from '../pipelines/unassign'

import type { RouteOpStrategy } from './index'
import type { Deps, OperationPlan, RouteOpIntent, RouteOpsStrategy } from 'types/modules/routing'

const STRATEGY_ID: RouteOpsStrategy = 'v2-pipeline'

export const v2PipelineStrategy: RouteOpStrategy = {
  id: STRATEGY_ID,
  plan(intent: RouteOpIntent, deps: Deps): OperationPlan {
    switch (intent.kind) {
      case 'unassign':
        return buildUnassignPipeline(intent, deps)
      case 'assign':
        return buildAssignPipeline(intent, deps)
      case 'move':
        return buildMovePipeline(intent, deps)
      case 'resequence':
        return buildResequencePipeline(intent, deps)
      case 'complete':
        return buildCompletePipeline(intent, deps)
      case 'change-date':
        return buildChangeDatePipeline(intent, deps)
      case 'change-day-of-week':
        return buildChangeDayOfWeekPipeline(intent, deps)
      case 'master-publish':
        return buildMasterPublishPipeline(intent, deps)
      case 'restore-master-version':
        return buildRestoreMasterVersionPipeline(intent, deps)
      case 'restore-daily-version':
        return buildRestoreDailyVersionPipeline(intent, deps)
      case 'sync-daily-to-master':
        return buildSyncDailyToMasterPipeline(intent, deps)
      default:
        return assertNever(intent)
    }
  },
}
